import { type ReactNode, type RefObject, useEffect } from 'react';
import { noop } from 'lodash';
import { returnNull } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  DisabledZoomControls,
  updateViewContext,
  updateViewProperties,
} from '@ardoq/view-settings';
import { SettingsBarAndViewContainer } from 'tabview/SettingsBarAndViewContainer';
import type {
  HierarchicalViewpointViewId,
  HierarchicalViewpointViewProperties,
  HierarchicalViewpointViewSettings,
} from './types';

type HierarchicalViewpointShellProps<
  Settings extends HierarchicalViewpointViewSettings,
> = {
  viewId: HierarchicalViewpointViewId;
  properties: HierarchicalViewpointViewProperties<Settings>;
  viewContainer: RefObject<HTMLDivElement | null>;
  children: ReactNode;
};

/**
 * Publishes the view's settings and context to the one panel viewpoint mode has for them.
 *
 * There is no settings bar here and no branch deciding whether to draw one. Viewpoint mode puts
 * every view's controls in `viewModifiers$`, so a view that only ever runs in viewpoint mode has
 * nothing to render above itself — where the shared shell had to ask `isViewpointMode` twice, once
 * to decide whether to publish and once to decide whether to draw.
 *
 * The flex column stays: the visualization containers are `flex: 1` and have no height without it.
 * These views have no legend, and `noop` is what disables the header's legend button.
 */
export const HierarchicalViewpointShell = <
  Settings extends HierarchicalViewpointViewSettings,
>({
  viewId,
  properties,
  viewContainer,
  children,
}: HierarchicalViewpointShellProps<Settings>) => {
  useEffect(() => {
    dispatchAction(
      updateViewProperties({ viewId, viewProperties: properties })
    );
    dispatchAction(
      updateViewContext({
        getContainer: () => viewContainer.current,
        getGraphComponent: returnNull,
        optimizeLayout: null,
        zoomIn: null,
        zoomOut: null,
        zoomControls: null,
        zoomControlsDisabledState: DisabledZoomControls.NONE,
        toggleLegend: noop,
        isLegendActive: false,
      })
    );
  });

  return <SettingsBarAndViewContainer>{children}</SettingsBarAndViewContainer>;
};
